import { useTranslation } from 'react-i18next';
import { HandCoins, TrendingUp, Shield } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

const WhySection = () => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const reasons = [
    {
      icon: <HandCoins className="w-12 h-12" />,
      iconBg: 'bg-orange-100',
      iconColor: 'text-accent-orange',
      title: t('why.reason1Title'),
      description: t('why.reason1Desc'),
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      iconBg: 'bg-blue-100',
      iconColor: 'text-primary',
      title: t('why.reason2Title'),
      description: t('why.reason2Desc'),
    },
    {
      icon: <Shield className="w-12 h-12" />,
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      title: t('why.reason3Title'),
      description: t('why.reason3Desc'),
    },
  ];

  return (
    <section ref={sectionRef} className="section-spacing py-12 sm:py-16 md:py-20 lg:py-24 xl:py-28 relative overflow-hidden" style={{ background: 'linear-gradient(180deg, #00C9A7 0%, #00A98E 100%)' }}>
      {/* Angled top edge */}
      <div className="absolute top-0 left-0 right-0 h-12 sm:h-16 md:h-20 bg-white" style={{ clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 0)' }} />
      
      <div className="container-custom relative z-10 px-4 sm:px-6">
        {/* Section Header */}
        <div className={`text-center mb-6 sm:mb-8 md:mb-10 lg:mb-12 transition-all duration-700 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <h2 className="hero-headline text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-white mb-3 sm:mb-4 px-2">
            {t('why.title')}
          </h2>
        </div>

        {/* Reasons Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 lg:gap-12">
          {reasons.map((reason, index) => (
            <div 
              key={index} 
              className={`text-center transition-all duration-700 px-2 ${
                isVisible 
                  ? 'opacity-100 translate-y-0' 
                  : 'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${200 + index * 150}ms` }}
            >
              {/* Icon */}
              <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl ${reason.iconBg} ${reason.iconColor} mb-3 sm:mb-4 md:mb-6 transition-all duration-500 hover:scale-110 hover:rotate-3 ${
                isVisible ? 'scale-100 rotate-0' : 'scale-75 -rotate-12'
              }`} style={{ transitionDelay: `${300 + index * 150}ms` }}>
                <div className="w-7 h-7 sm:w-8 sm:h-8 md:w-12 md:h-12 flex items-center justify-center">
                  {reason.icon}
                </div>
              </div>
              
              {/* Title */}
              <h3 className="text-base sm:text-lg md:text-xl font-bold text-white mb-2 sm:mb-3 leading-tight">
                {reason.title}
              </h3>
              
              {/* Description */}
              <p className="text-sm sm:text-base text-white/95 leading-relaxed max-w-sm mx-auto">
                {reason.description}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Angled bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-12 sm:h-16 md:h-20 bg-gray-50" style={{ clipPath: 'polygon(0 100%, 100% 0, 100% 100%, 0 100%)' }} />
    </section>
  );
};

export default WhySection;
